
import { Component, Prop } from 'vue-property-decorator';
import Page from '@/Page.vue';
import { AlertEscalationModel, AlertEscalationTypeEnum, TenantUserModel } from '@/libs/Api';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

type SelectItem = { header?: string; divider?: boolean; text?: string; value?: string };

@Component({})
export default class SettingsAlertRecipientSelect extends Page {
    @Prop({ default: () => [] }) readonly value!: AlertEscalationModel[];
    @Prop({ default: () => [] }) tenantUsers!: TenantUserModel[];
    @Prop({ default: () => [] }) rules!: Array<(v: any) => true | string>;

    localUsers: TenantUserModel[] = [];

    get users(): TenantUserModel[] {
        return this.tenantUsers && this.tenantUsers.length > 0 ? this.tenantUsers : this.localUsers;
    }

    async mounted() {
        if (!this.tenantUsers || this.tenantUsers.length === 0) {
            tenantUsersStore.dispatch(CrudAction.GetAll, createCrudQueryPayload());
            const items = (await this.subscribe(tenantUsersStore, CrudReponse.GetAll)) as TenantUserModel[];
            this.localUsers = items ?? [];
        }
    }

    get items(): SelectItem[] {
        const items: SelectItem[] = [];
        // Groups (fixed order)
        items.push({ header: this.$t('settings.categories.escalationTypes') as string });
        items.push({ text: this.$t('settings.categories.escalationType.owner') as string, value: 'role:Owner' });
        items.push({
            text: this.$t('settings.categories.escalationType.directSupervisor') as string,
            value: 'role:DirectSupervisor',
        });
        items.push({
            text: this.$t('settings.categories.escalationType.secondLevelSupervisor') as string,
            value: 'role:SecondLevelSupervisor',
        });
        items.push({
            text: this.$t('settings.categories.escalationType.thirdLevelSupervisor') as string,
            value: 'role:ThirdLevelSupervisor',
        });
        // Users (sorted by displayName)
        items.push({ divider: true });
        items.push({ header: this.$t('settings.categories.recipientUsers') as string });
        const sortedUsers = [...this.users].sort((a, b) =>
            (a.user?.displayName ?? '').localeCompare(b.user?.displayName ?? '', 'cs')
        );
        for (const u of sortedUsers) {
            const text = u.user?.displayName ?? '';
            if (!u.id || !text) continue;
            items.push({ text, value: `user:${u.id}` });
        }
        return items;
    }

    get selectedKeys(): string[] {
        const escalations = this.value ?? [];
        const roleOrder: Array<keyof typeof AlertEscalationTypeEnum> = [
            'Owner',
            'DirectSupervisor',
            'SecondLevelSupervisor',
            'ThirdLevelSupervisor',
        ];
        const roleKeys = roleOrder
            .filter((r) => escalations.some((e) => e.type === AlertEscalationTypeEnum[r]))
            .map((r) => `role:${r}`);

        const userIds = escalations
            .filter((e) => e.type === AlertEscalationTypeEnum.TenantUser && !!e.tenantUserId)
            .map((e) => e.tenantUserId!) as string[];
        const userById = new Map(this.users.map((u) => [u.id!, u.user?.displayName ?? '']));
        const sortedUserIds = [...new Set(userIds)].sort((a, b) =>
            (userById.get(a) ?? '').localeCompare(userById.get(b) ?? '', 'cs')
        );
        const userKeys = sortedUserIds.map((id) => `user:${id}`);

        return [...roleKeys, ...userKeys];
    }

    onChange(keys: string[]) {
        const next: AlertEscalationModel[] = [];
        const unique = new Set(keys);
        unique.forEach((k) => {
            if (k.startsWith('user:')) {
                const id = k.substring('user:'.length);
                next.push({ type: AlertEscalationTypeEnum.TenantUser, tenantUserId: id } as AlertEscalationModel);
            } else if (k.startsWith('role:')) {
                const role = k.substring('role:'.length) as keyof typeof AlertEscalationTypeEnum;
                if (role in AlertEscalationTypeEnum) {
                    next.push({ type: AlertEscalationTypeEnum[role] as any } as AlertEscalationModel);
                }
            }
        });
        this.$emit('input', next);
    }

    get modelKeys(): string[] {
        return this.selectedKeys;
    }
    set modelKeys(keys: string[]) {
        this.onChange(keys);
    }

    get rulesInternal(): Array<(v: any) => true | string> {
        return this.rules && this.rules.length > 0
            ? this.rules
            : [(v: any) => (Array.isArray(v) && v.length > 0) || (this.$t('validation.thisFieldIsRequired') as string)];
    }
}
